import { BybButton } from "@Components/common/byb-ds";
import BybListSubheader from "@Components/common/byb-ds/lists/byb-list-subheader";
import { ListItemCell, ListItemLayout } from "@Components/game/common";
import { PlaylistDialog, PlaylistFilterDialog, PlaylistListItem, PlaylistSearchField } from "@Components/game/playlists";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import ContainerWrapper from "@Components/generic/container-wrapper";
import Icon from "@Components/generic/icon";
import TitleWrapper from "@Containers/shared/title-wrapper";
import { faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { useApi, useGlobalContext } from "@hooks";
import useI18n from "@i18n";
import { Box, Container, List, Stack, Typography, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { PlaylistResponse } from "@Shared/schemas/playlists/playlistSchemas";
import { useEffect, useMemo, useState } from "react";

const CustomPlaylists = () => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const [searchFilter, setSearchFilter] = useState<string>();
  const [filter, setFilter] = useState<(playlist: PlaylistResponse) => boolean>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<PlaylistResponse | undefined>(undefined);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use the new useApi hook with playlist v2 API
  const {
    call: getPlaylists,
    data: playlists,
    loading,
  } = useApi<typeof Api.Playlists.GetPlaylists>({
    api: Api.Playlists.GetPlaylists,
    request: {
      account_id: globalState.account?.id! as number,
      unpublished_allowed: true,
      // Add any additional filter parameters as needed
    },
  });

  // Setup delete playlist API
  const { call: deletePlaylist } = useApi<typeof Api.Playlists.DeletePlaylist>({
    api: Api.Playlists.DeletePlaylist,
  });

  useEffect(() => {
    if (globalState.account?.id) {
      getPlaylists();
    }
  }, [globalState.account?.id]);

  // Apply search and filter
  const playlistsSearchFiltered = useMemo(() => filterResults(playlists || [], searchFilter), [playlists, searchFilter]);

  const playlistsToRender = useMemo(
    () => (!!filter ? playlistsSearchFiltered.filter(filter) : playlistsSearchFiltered),
    [playlistsSearchFiltered, filter]
  );

  const handleEdit = (playlist: PlaylistResponse) => {
    setSelectedPlaylist(playlist);
    setEditDialogOpen(true);
  };

  const handleClose = (refresh?: boolean) => {
    setEditDialogOpen(false);
    setSelectedPlaylist(undefined);
    if (refresh) {
      // Refresh the playlist list if needed
      getPlaylists();
    }
  };

  const handleDelete = async (playlist: PlaylistResponse) => {
    setIsDeleting(true);
    try {
      await deletePlaylist({
        playlist_id: playlist.id,
        archive_only: false,
      });
      getPlaylists();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ContainerWrapper id="CUSTOM-PLAYLISTS" enableBackButton={false}>
      <Container maxWidth={false} sx={{ padding: theme.spacing(6, 0) }}>
        {(loading || isDeleting) && <CenteredLoadingOverlay loading={loading || isDeleting} />}
        <TitleWrapper
          title={i18n("playlists.page.title")}
          headerContent={
            <Stack direction="row" spacing={2} sx={{ padding: theme.spacing(0.2, 0) }}>
              <PlaylistSearchField placeholder={i18n("search")} value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} />
              {/* Todo - Fix functionality and re-enable
              <BybButton 
                color="secondary" 
                icon={<Icon icon={faSliders} />} 
                onClick={() => setFiltersOpen(true)} 
              /> */}
              <BybButton color="primary" icon={<Icon icon={faPlusCircle} />} onClick={() => setEditDialogOpen(true)}>
                {i18n("button.create")}
              </BybButton>
            </Stack>
          }
        >
          <List
            subheader={
              <Box>
                <BybListSubheader>
                  <ListItemLayout columns={8}>
                    <ListItemCell subHeader first colSpan={2} sx={{ textAlign: "left" }}>
                      <Typography variant="textBase600" color="text.secondary">
                        {i18n("playlists.custom.name")}
                      </Typography>
                    </ListItemCell>
                    <ListItemCell subHeader colSpan={1} sx={{ textAlign: "center" }}>
                      <Typography variant="textBase600" color="text.secondary">
                        {i18n("playlists.custom.start")}
                      </Typography>
                    </ListItemCell>
                    <ListItemCell subHeader colSpan={1} sx={{ textAlign: "center" }}>
                      <Typography variant="textBase600" color="text.secondary">
                        {i18n("playlists.custom.end")}
                      </Typography>
                    </ListItemCell>
                    <ListItemCell subHeader colSpan={1} sx={{ textAlign: "center" }}>
                      <Typography variant="textBase600" color="text.secondary">
                        {i18n("playlists.custom.scenarios")}
                      </Typography>
                    </ListItemCell>
                    <ListItemCell subHeader colSpan={1} sx={{ textAlign: "center" }}>
                      <Typography variant="textBase600" color="text.secondary">
                        {i18n("playlists.custom.published")}
                      </Typography>
                    </ListItemCell>
                    <ListItemCell subHeader colSpan={1} sx={{ textAlign: "center" }}>
                      <Typography variant="textBase600" color="text.secondary">
                        {i18n("playlists.custom.updated")}
                      </Typography>
                    </ListItemCell>
                    <ListItemCell subHeader colSpan={2}>
                      {""}
                    </ListItemCell>
                  </ListItemLayout>
                </BybListSubheader>
              </Box>
            }
          >
            {playlistsToRender.map((playlist: PlaylistResponse) => (
              <PlaylistListItem
                key={playlist.id}
                playlist={playlist}
                onRefresh={() => getPlaylists()}
                onEdit={() => handleEdit(playlist)}
                onDelete={() => handleDelete(playlist)}
              />
            ))}
          </List>
        </TitleWrapper>
        {editDialogOpen && (
          <PlaylistDialog
            open={editDialogOpen}
            title={selectedPlaylist ? i18n("playlists.custom.edit") : i18n("playlists.custom.create")}
            onClose={handleClose}
            playlist={selectedPlaylist}
          />
        )}
        <PlaylistFilterDialog
          open={filtersOpen}
          onApply={(filter: (playlist: PlaylistResponse) => boolean) => setFilter(() => filter)}
          onClose={() => setFiltersOpen(false)}
        />
      </Container>
    </ContainerWrapper>
  );
};

function filterResults(rows: PlaylistResponse[], filter?: string) {
  return filter
    ? rows.filter((r) => {
        const f = filter.toLocaleLowerCase();
        return r.name?.toLocaleLowerCase().includes(f) || r.description?.toLocaleLowerCase().includes(f);
      })
    : rows;
}

export default CustomPlaylists;
